import {
    Box,
    Button,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import {blue, grey} from "@mui/material/colors";
import {useParams} from "react-router-dom";
import {getCustomerBillingInvoiceDetail} from "@/services/customer.ts";

import dayjs from "dayjs";
import React, {useEffect, useState} from "react";
import {logging} from "@/utils/functions.ts";

import AccountBilled from "../../shared/AccountBilled";

import {LoadingBackdrop} from "@/components/Layout/DefaultLayout";
import Utils from "@/components/Shared/Utils";
import AccountBillingInvoicePayment from "../InvoicePayment";
import UICard, {UICardContent, UICardHeadingLight} from "@/components/Shared/Utils/UICard";

export default function AccountBillingInvoice({customer}: any) {
    const params = useParams();

    const {data: invoice} = getCustomerBillingInvoiceDetail(params?.invoice_id);

    const [active, setActive] = useState<null | boolean>(null);

    function handleTranslate(string: string) {
        return string
            ?.toLowerCase()
            ?.replace("gia hạn", "RENEW")
            ?.replace("đăng kí mới", "REGISTER")
            ?.replace("bán mới", "REGISTER")
            ?.replace("tháng", "MONTH")
            ?.replace("tỉ giá", "EXCHANGE")
            ?.replace("nâng thêm", "RESIZE")
            ?.toUpperCase();
    }

    useEffect(() => {
        logging("AccountBillingInvoice ▶ Customer", customer);
        logging("AccountBillingInvoice ▶ CustomerBillingInvoiceDetail", invoice);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <Utils.LayoutUtils.MainContainer modal={active !== null}>
                <React.Suspense fallback={<LoadingBackdrop/>}>
                    <Utils.LayoutUtils.MainWrapper>
                        <Utils.LayoutUtils.MainHeading
                            title={`Invoice Information — ${invoice?.invoice_id}`}
                            description={`Created Date: ${dayjs(invoice?.invoice_created_at)?.format("MMM DD, YYYY")}`}
                        />
                        <UICard gradient>
                            <UICardHeadingLight title={invoice.invoice_status === 2 ? "Paid" : "Unpaid"}
                                                description={invoice?.invoice_description}/>
                            <UICardContent>
                                <Box p={2}>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-bettwen"
                                        sx={{mb: 2}}
                                    >
                                        <Box width="100%">
                                            <Typography fontSize={14} color={grey[700]} sx={{mb: 1}}>
                                                Billed To
                                            </Typography>
                                            <AccountBilled customer={customer}/>
                                        </Box>
                                        {invoice?.invoice_status === 1 && !active && (
                                            <Box width="100%">
                                                <Stack direction="row" justifyContent="end">
                                                    <Button
                                                        onClick={() => setActive(true)}
                                                        sx={{
                                                            border: `1px solid ${blue[900]}`,
                                                            bgcolor: "transparent ! important",
                                                        }}
                                                    >
                                                        <Typography fontSize={14} sx={{color: blue[900]}}>
                                                            Make Payment
                                                        </Typography>
                                                    </Button>
                                                </Stack>
                                            </Box>
                                        )}
                                    </Stack>
                                    <Box>
                                        <Typography fontSize={14} color={grey[700]} sx={{mb: 1}}>
                                            Details
                                        </Typography>
                                        <Table
                                            size="small"
                                            sx={{
                                                tableLayout: "fixed",
                                            }}
                                        >
                                            <TableHead sx={{bgcolor: grey[100]}}>
                                                <TableRow>
                                                    <TableCell
                                                        sx={{width: "40%", fontSize: 13, fontWeight: 700}}
                                                    >
                                                        Item
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{width: "10%", fontSize: 13, fontWeight: 700}}
                                                    >
                                                        Qty
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{width: "10% ", fontSize: 13, fontWeight: 700}}
                                                    >
                                                        Unit
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{width: "10%", fontSize: 13, fontWeight: 700}}
                                                    >
                                                        Tax
                                                    </TableCell>
                                                    <TableCell sx={{width: "20%"}}>Amount (VND)</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {invoice?.invoice_items?.map((item: any) => {
                                                    if (item.invoice_item_hidden) return null;

                                                    return (
                                                        <TableRow key={item.invoice_item_id}>
                                                            <TableCell sx={{fontSize: 13}}>
                                                                {handleTranslate(item.invoice_item_name || item.invoice_item_name)}
                                                            </TableCell>
                                                            <TableCell sx={{fontSize: 13}}>
                                                                {item.invoice_item_value}
                                                            </TableCell>
                                                            <TableCell sx={{fontSize: 13}}>
                                                                {item.invoice_item_unit || "-"}
                                                            </TableCell>
                                                            <TableCell sx={{fontSize: 13}}>
                                                                {(item.invoice_item_tax && "√") || "-"}
                                                            </TableCell>
                                                            <TableCell sx={{fontSize: 13}}>
                                                                {item.invoice_item_amount?.toLocaleString() || "-"}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}

                                                <TableRow>
                                                    <TableCell/>
                                                    <TableCell/>
                                                    <TableCell/>
                                                    <TableCell sx={{fontSize: 13, px: 0, textAlign: "right"}}>
                                                        Sub Total
                                                    </TableCell>
                                                    <TableCell sx={{fontSize: 13, fontWeight: 700}}>
                                                        {invoice.invoice_subtotal?.toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell/>
                                                    <TableCell/>
                                                    <TableCell/>
                                                    <TableCell sx={{fontSize: 13, px: 0, textAlign: "right"}}>
                                                        Tax @10%
                                                    </TableCell>
                                                    <TableCell sx={{fontSize: 13, fontWeight: 700}}>
                                                        {invoice.invoice_tax?.toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow sx={{bgcolor: grey[50]}}>
                                                    <TableCell/>
                                                    <TableCell/>
                                                    <TableCell/>
                                                    <TableCell sx={{fontSize: 13, px: 0, textAlign: "right"}}>
                                                        Total
                                                    </TableCell>
                                                    <TableCell sx={{fontSize: 13, fontWeight: 700}}>
                                                        {invoice.invoice_total?.toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Box>
                                </Box>
                            </UICardContent>
                        </UICard>
                    </Utils.LayoutUtils.MainWrapper>
                </React.Suspense>
            </Utils.LayoutUtils.MainContainer>
            {active && (
                <Utils.LayoutUtils.MainAttachedSidebar>
                    <React.Suspense fallback={<LoadingBackdrop/>}>
                        <AccountBillingInvoicePayment customer={customer} close={() => setActive(null)}/>
                    </React.Suspense>
                </Utils.LayoutUtils.MainAttachedSidebar>
            )}
        </>
    );
}
